import { createSelector, createSlice, isAnyOf } from '@reduxjs/toolkit';
import { authApiSlice } from './auth-api.slice';

export interface Application {
    applicationId: string;
    isEnvApp: boolean;
}

interface RegistrationState {
    userId?: string;
    userFirstName?: string;
    userLastName?: string;
    phoneContact?: {
        contactId?: string;
        countryCode: string;
        number: string;
    };
    authenticator?: {
        qrCodeUri: string;
        secret: string;
    };
    application?: Application;
}

const initialState: RegistrationState = {
    userId: undefined,
    phoneContact: undefined,
    authenticator: undefined,
    application: undefined
};

/**
 * Slice for registration info
 */
export const registrationSlice = createSlice({
    name: 'registration',
    initialState,
    reducers: {},
    extraReducers: builder => {
        builder
            .addMatcher(authApiSlice.endpoints.enrollmentEmailSubmit.matchFulfilled, (state, action) => {
                state.userId = action.payload.userId;
                state.userFirstName = action.payload.userFirstName;
                state.userLastName = action.payload.userLastName;
            })
            .addMatcher(authApiSlice.endpoints.enrollmentPhoneInit.matchFulfilled, (state, action) => {
                const { number, countryCode } = action.meta.arg.originalArgs;

                state.phoneContact = {
                    number,
                    countryCode
                };
            })
            .addMatcher(authApiSlice.endpoints.enrollmentPhoneInit.matchFulfilled, (state, action) => {
                const { contactId } = action.payload;
                if (state.phoneContact) {
                    state.phoneContact.contactId = contactId;
                }
            })
            .addMatcher(authApiSlice.endpoints.enrollmentPasswordSubmit.matchFulfilled, (state, action) => {
                state.phoneContact = action.payload.unverifiedPhoneContact;
            })
            .addMatcher(
                isAnyOf(
                    authApiSlice.endpoints.enrollmentPhoneSubmit.matchFulfilled,
                    authApiSlice.endpoints.enrollmentPhoneSkip.matchFulfilled
                ),
                (state, action) => {
                    // When no payload is provided, we want to return the user to the tiles page, return initialState with no application
                    if (!action.payload) {
                        return initialState;
                    }

                    const { appId, isEnvApp } = action.payload;
                    const app = appId
                        ? {
                              applicationId: appId,
                              isEnvApp: !!isEnvApp
                          }
                        : undefined;
                    return {
                        // clear state when registration is complete, except for application
                        ...initialState,
                        application: app
                    };
                }
            )
            .addMatcher(authApiSlice.endpoints.enrollmentAuthenticatorRegister.matchFulfilled, (state, action) => {
                state.authenticator = action.payload;
            });
    }
});

export const selectSelf = rootState => rootState[registrationSlice.name] || initialState;
export const selectUserEmail = createSelector(selectSelf, state => state.userEmail);
export const selectPhoneContact = createSelector(selectSelf, state => state.phoneContact);
export const selectAuthenticator = createSelector(selectSelf, state => state.authenticator);
export const selectRegistrationState = createSelector(selectSelf, state => state);
export const selectUserId = createSelector(selectSelf, state => state.userId);
export const selectUserFirstName = createSelector(selectSelf, state => state.userFirstName);
export const selectUserLastName = createSelector(selectSelf, state => state.userLastName);
